import { Component, Input, ChangeDetectionStrategy, ChangeDetectorRef, OnInit, ViewEncapsulation, EventEmitter, Output } from '@angular/core';

import { Media, MediaType, Mote, MediaEvent, MediaEventType, MediaState, MoteState, MoteEventType } from '../../models';
import { AutoSub, AutoSubOnDestroy, GroupService, MoteEventService, UserService } from '../../providers';
import { MotesMediaPlayerService } from '../../motes-media-player/media-player.service';

export enum PlayIcon {
    play = 'play',
    pause = 'pause',
    stop = 'stop',
    sound = 'sound'
}

@AutoSubOnDestroy()
@Component({
    selector: 'motes-media',
    templateUrl: 'motes-media.html',
    styleUrls: ['./motes-media.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Default
})
export class MotesMediaComponent implements OnInit {
    @Input('media') set setMedia(media: Media) {
        if(!media) {
            // console.warn("No media element in input - cant show media");
            return;
        }

        this.media = media;

        // Disable full screen if there is no image to show
        this.preventImgFullscreen = !(this.aspectRatio == 1 && this.media.square.url) && !(this.aspectRatio != 1 && this.media.hd.url);
        let isSound = media.type == MediaType.SOUND || media.type == MediaType.AMBISONIC;
        let isVideo = media.type == MediaType.VIDEO || media.type == MediaType.FILMCENTRALEN_VIDEO || media.type == MediaType.YOUTUBE_VIDEO;
        this.showPlayOverlay = isSound || isVideo;
    }

    @Input() mote: Mote;
    @Input() aspectRatio: number = (16 / 9);
    @Input() alt: string = "Motes Image";
    @Input() bgColor: string = "#444";
    @Input() detailedView: boolean;
    @Input() arrayIdx: number;
    // Show 'sound' icon on SOUND media. Default is 'play' icon.
    @Input() showSoundIcon: boolean = false;
    // Disable fullscreen for image click
    @Input() preventImgFullscreen: boolean = false;
    // Disable fullscreen for media (image, video) and playing (audio, video)
    @Input() preventPlayMedia: boolean = false;

    @Output() mediaClicked = new EventEmitter<void>();
    @Output() mediaEvent = new EventEmitter<MediaEvent>();

    public media: Media;
    public mediaLoading: boolean = false;
    public mediaLoadProgress: number = 0;
    public imageHasLoaded: boolean = false;
    public showPlayOverlay: boolean = false;
    public playIcon: PlayIcon = PlayIcon.play;
    public showLoading: boolean = false;

    // Export 
    public MediaType = MediaType;
    public MediaState = MediaState;

    constructor(private cd: ChangeDetectorRef, public mediaPlayerService: MotesMediaPlayerService, private moteEventService: MoteEventService, private userService: UserService, private groupService: GroupService) {
    }

    ngOnInit() {
        // Init play icon according to current player state
        let playerEv = this.mediaPlayerService.currentEvent;
        this.playIcon = this.selectPlayIcon(playerEv.state, playerEv.media);

        AutoSub(this).reg['mediaEv'] = this.mediaPlayerService.watchMediaEvents().subscribe((event: MediaEvent) => {
            this.showLoading = event?.media?.key == this.media?.key;

            if(event.type == MediaEventType.LOAD_PROGRESS) {
                // Update the buffering progress (only used for now in augmented player)
                this.mediaLoading = true;
            }

            if(event.type == MediaEventType.VERSION_ERROR) {
                this.mediaLoading = false;
            }

            if(event.type != MediaEventType.STATEUPDATE) return;

            // Save current player state easy access in view
            this.playIcon = this.selectPlayIcon(event.state, event.media);

            if(event.state != MediaState.PLAYING) {
                this.mediaLoading = false;
            }
            // Explicitly invoke change detection upon `STATEUPDATE` events
            this.cd.detectChanges();
        });

        // Show loading indicator until map is ready to show 3D experience
        AutoSub(this).reg['motesEv'] = this.moteEventService.watchEventType(MoteEventType.SHOW_3D_MOTE_ON_MAP).subscribe((ev) => {
            this.mediaLoading = false;
        });
    }

    private selectPlayIcon(state: MediaState, playerMedia: Media): PlayIcon {
        let defaultIcon = this.showSoundIcon && this.media?.type == MediaType.SOUND ? PlayIcon.sound : PlayIcon.play;
        // Check if the media player is playing media from another mote
        if(!playerMedia || !this.media) return defaultIcon;
        let isPlayingForeign = (playerMedia.key != this.media.key);
        // Show stop icon when playing/pausing foreign media
        if(state == MediaState.PLAYING && isPlayingForeign) return PlayIcon.play;
        // Show pause if the media of this mote is currently playing
        if(state == MediaState.PLAYING && !isPlayingForeign) return PlayIcon.pause;
        // Show play icon when media is just loaded or stopped
        return defaultIcon;
    }

    public isAvailable() {
        if(!this.mote) return true;
        return this.mote.moteState == MoteState.UNLOCKED ||
            this.mote.moteState == MoteState.USERMOTE;
    }

    public playMedia(): void {
        this.mediaClicked.emit();
        if(this.preventPlayMedia) return;

        let mediaInitialized = (this.mediaPlayerService.currentEvent.media && this.media);
        let isCurrentMedia = (!mediaInitialized || this.mediaPlayerService.currentEvent.media.key == this.media.key);

        // If the MediaType is IMAGE, show the image
        if(this.media.type == MediaType.IMAGE) {
            if(!this.preventImgFullscreen) this.mediaPlayerService.showFullscreenImage(this.mote, this.media);
        }
        // Else, the MediaType is a type of audio or video, and playback should be handled based on MediaState of the current event
        else {
            switch(this.mediaPlayerService.currentEvent.state) {
                case MediaState.NONE:
                case MediaState.LOADED:
                case MediaState.VERSION_ERROR:
                    // Start player and request group play
                    this.startPlayer(this.mote, this.media, true);
                    break;

                case MediaState.PAUSED:
                    // Start player (only request group play, if current media is not the same as already playing)
                    this.startPlayer(this.mote, this.media, !isCurrentMedia);
                    break;

                default:
                    this.mediaPlayerService.pausePlayer();
                    if(!isCurrentMedia) this.startPlayer(this.mote, this.media, true);
                    break;
            }
        }
    }

    public imageLoaded() {
        this.imageHasLoaded = true;
    }

    private startPlayer(mote: Mote, media: Media, requestGroupPlay: boolean) {
        // Request group playback: If the user is part of a group, a GroupEvent of type GROUP_PLAYBACK_REQUEST is saved to the DB
        const groupPlayback = (this.groupService.isGroupPlayEnabled && requestGroupPlay) ? this.requestGroupPlayback(this.mote, this.media) : false;
        // If group playback was requested, the media player should await group members before starting
        if(!groupPlayback) this.mediaPlayerService.startPlayer(this.mote, this.media);
    }

    /**
     * Requests group playback if the current user is part of a group.
     * @returns `true`, if the user is part of a group. Otherwise, `false`
     */
    private requestGroupPlayback(mote: Mote, media: Media): boolean {
        // Reject everything but sound media
        if(media.type != MediaType.SOUND) return false;

        // Get the currently cached user state (it's important this is not async, as preparePlayer() needs to be invoked immediately)
        const currentUser = this.userService.currentUser;

        // If the user is not part of a group, group playback should not be initiated
        if(!currentUser.groupKey) return false;

        // Prepare media player to play when group is ready 
        this.mediaPlayerService.preparePlayer(mote, media);
        // Set user state ready and notify group
        this.groupService.startGroupPlay(currentUser, mote.key, media.key);
        return true;
    }
}
